import logger from '@/lib/logger';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ResultsView } from '@/features/measurement/ResultsView';
import { apiClient } from '@/lib/api';

/**
 * ResultsViewPage — thin routing wrapper for ResultsView.
 *
 * Validates the test can be opened, then delegates all display logic to ResultsView.
 * Works for both post-measurement results (classifying) and historical records (complete/failed).
 */
export function ResultsViewPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPath = searchParams.get('from') || '/';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!testId) { setError('No test ID provided'); setLoading(false); return; }
    fetchTestResults(testId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  const fetchTestResults = async (id: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ id: string; status: string }>(`/api/v1/tests/${id}`);
      const testData = response.data;

      // Allow opening tests that are classifying (just finished measurement) or final
      const openableStatuses = ['complete', 'failed', 'classification_failed', 'classifying'];
      const testStatus = testData.status?.toLowerCase() || '';

      if (!openableStatuses.includes(testStatus)) {
        if (testStatus === 'cancelled') {
          setError('This test was cancelled and cannot be viewed.');
        } else if (testStatus.includes('baseline') || testStatus.includes('specimen') || testStatus === 'running') {
          setError('This test is still in progress. Please wait for it to complete before viewing.');
        } else {
          setError(`This test cannot be viewed. Current status: ${testStatus}`);
        }
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (err) {
      logger.error('Failed to fetch test results:', err);
      setError('Failed to load test results. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate(fromPath);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100vw', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Loading test results...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100vw', gap: 2, p: 3 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/tests')}>
            Go back to tests
          </Button>
          <Button variant="contained" onClick={() => navigate('/')}>
            Go to dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  return <ResultsView testId={testId!} onClose={handleClose} />;
}
